import React, { ReactNode } from 'react';
import { Alert, Form, Table } from 'react-bootstrap';
import Select from 'react-select';

import CurrencyControl from '../../../app/components/CurrencyControl';
import {
  CreateUpdateSalesInvoicePositionDto,
  CreateUpdateSalesInvoicePositionDtoVatEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { VatEnum } from '../../../common/spdCore/validation/enums';
import numberFormatter from '../../../common/numberFormatter';
import { UseSelectedGridRowReturn } from '../../../common/hooks/useSelectGridRow';
import { getGrossPrice } from '../../../utils/getVatAmountByVat';
import mathUtils from '../../../utils/mathUtils';

import SalesInvoicePositionsSumRow from './SalesInvoicePositionsSumRow';
import { SalesInvoicePositionNameSelector } from './SalesInvoicePositionNameSelector';
import { QuantityCell } from './QuantityCell';

interface SalesInvoicePositionsGridProps {
  select: UseSelectedGridRowReturn<number>;
  positions?: CreateUpdateSalesInvoicePositionDto[];
  propertyChange: (
    obj: Partial<CreateUpdateSalesInvoicePositionDto>,
    index: number,
  ) => void;
  readOnly?: boolean;
  parentAmountNet?: string;
  parentAmountGross?: string;
  isCorrection?: boolean;
}

const vatOptions = Object.entries(VatEnum).map(([key, value]) => ({
  value: key as unknown as CreateUpdateSalesInvoicePositionDtoVatEnum,
  label: value,
}));

const calculateGross = (
  amount: string | undefined,
  vat: string,
  quantity = 1,
): number => {
  const totalAmount = mathUtils.multiply(amount || '0', quantity.toString());
  const vatEnum = (vat as VatEnum) || VatEnum['0% KR'];
  const gross = getGrossPrice(totalAmount, vatEnum);
  return typeof gross === 'number' ? gross : mathUtils.parse(gross);
};

export interface TableColumn {
  header: string | ReactNode;
  isHidden?: boolean;
  tdCssClass?: string;
  tdStyle?: React.CSSProperties;
  renderCell: (
    item: CreateUpdateSalesInvoicePositionDto,
    isSelected: boolean,
  ) => string | ReactNode | null;
}

export const SalesInvoicePositionsGrid: React.FC<
  SalesInvoicePositionsGridProps
> = props => {
  const { select } = props;

  const columns: TableColumn[] = [
    {
      header: '',
      renderCell: (item, isSelected) =>
        item.isLocked || props.readOnly ? null : (
          <Form.Check
            type="checkbox"
            checked={isSelected}
            disabled={props.readOnly}
            onChange={e => {
              if (e.target.checked) {
                return select.handleSelect(item.order);
              }

              return select.handleUnselect(item.order);
            }}
          />
        ),
    },
    {
      header: 'Lp',
      renderCell: item => item.order,
    },
    {
      header: 'Nazwa',
      tdStyle: { width: '50%' },
      renderCell: item => (
        <SalesInvoicePositionNameSelector
          value={item.name || ''}
          readOnly={props.readOnly}
          onChange={value => props.propertyChange({ name: value }, item.order)}
        />
      ),
    },
    {
      header: 'Cena netto',
      renderCell: item => (
        <CurrencyControl
          value={item.amount || ''}
          decimalScale={2}
          disabled={props.readOnly}
          onChange={value =>
            props.propertyChange(
              {
                amount: value || '',
              },
              item.order,
            )
          }
        />
      ),
    },
    {
      header: 'Ilość',
      tdStyle: { width: '8%' },
      renderCell: item => (
        <QuantityCell
          quantity={item.quantity}
          isCorrection={props.isCorrection}
          readOnly={props.readOnly}
          onChange={quantity => props.propertyChange({ quantity }, item.order)}
        />
      ),
    },
    {
      header: 'Kwota netto',
      tdCssClass: 'amounts-align-right',
      renderCell: item => {
        const net = mathUtils.multiply(
          item.amount || '0',
          (item.quantity ?? 1).toString(),
        );
        return numberFormatter(net.toString(), true);
      },
    },
    {
      header: 'VAT',
      renderCell: item => (
        <Select
          value={
            item?.vat
              ? vatOptions.find(({ value }) => item?.vat === value)
              : null
          }
          isDisabled={props.readOnly}
          options={vatOptions}
          onChange={option =>
            props.propertyChange(
              {
                vat: option?.value,
              },
              item.order,
            )
          }
        />
      ),
    },
    {
      header: 'Kwota brutto',
      renderCell: item => {
        const gross = calculateGross(item.amount, item.vat, item.quantity ?? 1);
        return numberFormatter(gross.toString(), true);
      },
      tdCssClass: 'amounts-align-right',
    },
  ];

  if (!props.positions) {
    return <Alert variant="warning">Brak danych.</Alert>;
  }

  return (
    <>
      <Table striped>
        <thead>
          <tr>
            {columns
              .filter(({ isHidden }) => !isHidden)
              .map((col, index) => (
                <th key={index}>{col.header}</th>
              ))}
          </tr>
        </thead>
        <tbody>
          {props.positions.map(item => {
            const isSelected = select.selected.includes(item.order);

            return (
              <tr key={item.order} className="align-middle">
                {columns
                  .filter(({ isHidden }) => !isHidden)
                  .map((col, index) => (
                    <td
                      key={index}
                      className={col.tdCssClass}
                      style={col.tdStyle}
                    >
                      {col.renderCell(item, isSelected)}
                    </td>
                  ))}
              </tr>
            );
          })}
          <SalesInvoicePositionsSumRow
            positions={props.positions}
            parentAmountNet={props.parentAmountNet}
            parentAmountGross={props.parentAmountGross}
          />
        </tbody>
      </Table>
    </>
  );
};
